// OnboardingLogic.tsx
import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { usePerplexityAI } from "0.Mine/1.Hooks/AI/Perplexity";
import { UseHandleBusiness } from "0.Mine/1.Hooks/business/business";
import { useHandleBusinessEmployees } from "0.Mine/1.Hooks/business/employees";
import { useHandleImageUpload } from "0.Mine/1.Hooks/business/images";
import { useHandleBusinessSchedule } from "0.Mine/1.Hooks/business/openingTimes";
import { useHandleBusinessService } from "0.Mine/1.Hooks/business/services";
import { UseHandleKanban } from "0.Mine/1.Hooks/CRM/Kanban";
import { UseHandleLeads } from "0.Mine/1.Hooks/CRM/Leads";
import { Business } from "0.Mine/3.Interfaces/business";
import { Employee } from "0.Mine/3.Interfaces/Employee";
import { LeadState } from "0.Mine/3.Interfaces/lead";
import { BusinessService } from "0.Mine/3.Interfaces/Services";

import IntegrationDashboard from "../Container/AccountsForm/IntegrationDashboard";
import AddAnotherBusinessOnboarding from "../Container/AddAnotherBusiness";
import LocationForm from "../Container/BasicInfo/LocationForm";
import LogoForm from "../Container/BasicInfo/LogoForm";
import PlatformForm from "../Container/BasicInfo/PlatformForm";
import ProfileForm from "../Container/BasicInfo/ProfileForm";
import WebsiteForm from "../Container/BasicInfo/WebsiteForm";
import EmployeeForm from "../Container/EmployeesForm/EmployeeForm";
import BusinessScheduleOnboarding from "../Container/HoursForm/HoursForm";
import ServicesForm from "../Container/ServicesForm/ServicesForm";

import { ScheduleState } from "./HoursForm/HoursFormLogic";
import { defaultSchedule } from "./HoursForm/HoursFormLogic";

export const DEFAULT_BUSINESS: Business = {
  id: 0,
  userId: 0,
  name: "",
  category: "",
  phoneNumberPrefix: "",
  address: "",
  phoneNumber: "",
  websiteUrl: "",
  about: "",
  ownerFirstName: "",
  ownerLastName: "",
  businessTagLine: "",
  yearOfIncorporation: 0,
  email: "",
  city: "",
  country: "",
  postalCode: "",
  province: "",
  isFranchise: false,
  locationHidden: false,
};

export const fadeDuration = 300;

export const useOnboardingLogic = () => {
  // Central state for business data, services, employees, and step control.

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [businessData, setBusinessData] = useState<Partial<Business>>({});
  const [services, setServices] = useState<BusinessService[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [contentVisible, setContentVisible] = useState(true);
  const [schedule, setSchedule] = useState<ScheduleState>(defaultSchedule);
  const [loadingText, setLoadingText] = useState<string>("Preparing data...");
  const [stepIsValid, setStepIsValid] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [websiteUpdated, setWebsiteUpdated] = useState(false);
  const [infoFetched, setInfoFetched] = useState(false);
  const [servicesForBusiness, setServicesForBusiness] = useState<Record<string, BusinessService[]>>(
    {}
  );
  const [employeesForBusiness, setEmployeesForBusiness] = useState<Record<string, Employee[]>>({});
  const [scheduleForBusiness, setScheduleForBusiness] = useState<Record<string, ScheduleState>>({});
  const [businesses, setBusinesses] = useState<Business[]>([]);

  //Hooks
  const { addBusiness } = UseHandleBusiness();
  const { createBusinessSchedule } = useHandleBusinessSchedule();
  const { createService } = useHandleBusinessService();
  const { addEmployee } = useHandleBusinessEmployees();
  const navigate = useNavigate();
  const { uploadImage, uploadImageService } = useHandleImageUpload();
  const { CreateDefaultKanban } = UseHandleKanban();
  const { fetchBusinessInformationFromWebsite } = usePerplexityAI();
  const { createStatusOption } = UseHandleLeads();

  // Merge incoming data into the current business data.
  const updateBusinessData = useCallback((data: Partial<Business>) => {
    setBusinessData((prev) => ({ ...prev, ...data }));
  }, []);

  const setBusinessServices = useCallback((data: BusinessService[]) => {
    setServices(data);
  }, []);

  const setBusinessEmployees = useCallback((data: Employee[]) => {
    setEmployees(data);
  }, []);

  const setBusinessHours = useCallback((data: ScheduleState) => {
    setSchedule(data);
  }, []);

  const sleepRandom = async (): Promise<void> => {
    const delay = Math.random() * (2000 - 500) + 500; // random delay between 500ms and 2000ms
    return new Promise((resolve) => setTimeout(resolve, delay));
  };

  const updateWebsite = (data: Partial<Business>) => {
    console.log("Data!", data);
    updateBusinessData(data);
    setWebsiteUpdated(true);
  };

  const cloneBusiness = useCallback(async () => {
    setLoading(true);
    setLoadingText("Cloning business...");

    // 1. First save current business with its current data
    const originalBusiness = { ...businessData };
    const originalServices = [...services];
    const originalEmployees = [...employees];
    const originalSchedule = { ...schedule };

    // 2. Send current business to backend
    await finalizeBusiness(false);

    // 3. Create new "copy" business for the form
    setBusinessData({
      ...originalBusiness,
      id: 0, // Reset ID to ensure it saves as new
      name: `${originalBusiness.name || ""} (Copy)`.trim(),
    });

    // 4. Reset the form back to step 0 with cloned data
    setServices(originalServices);
    setEmployees(originalEmployees);
    setSchedule(originalSchedule);
    setActiveStep(0);
    setWebsiteUpdated(false);
    setInfoFetched(false);
    setLoading(false);
  }, [businessData, services, employees, schedule]);

  const startFromScratch = useCallback(async () => {
    setLoading(true);
    setLoadingText("Saving current business...");

    // 1. First save the current business to backend
    if (businessData.name || Object.keys(businessData).length > 0) {
      await finalizeBusiness(false);
    }

    // 2. Reset EVERYTHING to defaults/empty
    setLoadingText("Starting new business...");
    setBusinessData({});
    setServices([]);
    setEmployees([]);
    setSchedule(defaultSchedule);

    // 3. Reset UI to step 0
    setActiveStep(0);
    setWebsiteUpdated(false);
    setInfoFetched(false);
    setLoading(false);
  }, [businessData]);

  /*useEffect(() => {

        const fetchData = async () => {
            const response = await fetchBusinessInformationFromWebsite(businessData.websiteUrl);
            //create a loading bar as we wait for the response
            console.log(response);
            //Set default values for the business data
            setBusinessData((prev) => ({
                ...prev,
                name: response.businessName,
                about: response.businessDescription,
                email: response.email,
                address: response.address,
                postalCode: response.postalCode,
                city: response.city,
                country: response.country,
                province: response.province,
            }));
        }

        if (websiteUpdated && !infoFetched) {
            console.log("Business data updated:", businessData);
            setWebsiteUpdated(false);
            setInfoFetched(true);
            fetchData();
        }


    }, [websiteUpdated, infoFetched, businessData]);*/

  const finalizeBusiness = async (navigateToDashboard?: boolean) => {
    setLoading(true);

    const finalBusiness: Business = { ...DEFAULT_BUSINESS, ...businessData };
    console.log("Final business object:", finalBusiness);
    console.log("Final services: ", services);
    console.log("Final Employees: ", employees);
    console.log("FINAL SCHEDULE: ", schedule);

    //remove the session
    localStorage.removeItem("business_session_validated");
    // First we create the businesses.
    try {
      const { id, userId } = await addBusiness(finalBusiness);
      await uploadImage(businessData.logoUrl, "logo", id);
      await uploadImage(businessData.coverUrl, "cover", id);
      await sleepRandom();
      setLoadingText("Adding business hours");
      // Add the schedule
      const scheduleResponse = await createBusinessSchedule(schedule, id);
      await sleepRandom();

      //Add the services
      setLoadingText("Adding services");
      services.forEach(async (service) => {
        const serviceId = await createService(id, service);
        await uploadImageService(service.photoUrl, serviceId, id.toString());
      });
      await sleepRandom();
      //Add the employees
      setLoadingText("Adding employees");
      employees.forEach(async (employee) => {
        await addEmployee(id, employee);
      });
      await sleepRandom();
      await CreateDefaultKanban(id);

      //Add the default status options by using the LeadState enum
      const statusOptions = Object.values(LeadState).map((status) => ({
        id: Date.now().toString(),
        label: status,
        color: "#000000", // Default color, you can change this as needed
      }));
      statusOptions.forEach(async (status) => {
        await createStatusOption(id, status.label);
      });
      setLoadingText("Done!");
      setLoading(false);

      //set default business to the one selected

      if (!navigateToDashboard) return;

      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error(error);
      setLoadingText("An error occurred. Please try again later");
      await sleepRandom();
      navigate("/signin", { replace: true });
    }
  };
  // Define the steps with corresponding labels and components.
  const steps = [
    {
      label: t("Il tuo sito web, la tua vetrina!"),
      description: t(
        "Inserisci l’indirizzo del tuo sito web: la nostra AI lo analizzerà per creare automaticamente una descrizione perfetta per te."
      ),
      component: <WebsiteForm initialValue={""} updateBusinessData={updateWebsite} />,
    },
    {
      label: t("Ora, conosciamoci meglio!"),
      description: t(
        "Inserisci i principali dati della tua azienda: li useremo per personalizzare documenti, messaggi ai clienti e semplificare le integrazioni future. Più preciso sei ora, meno problemi avrai dopo!"
      ),
      component: (
        <PlatformForm
          website={""}
          updateBusinessData={updateBusinessData}
          businessData={businessData}
        />
      ),
    },
    {
      label: t("Il tuo profilo, la tua storia!"),
      description: t(
        "Più ci racconti, più cresciamo insieme! Condividi cosa fa la tua azienda: costruiremo per te strumenti su misura per lavorare meglio e attirare più clienti."
      ),
      component: (
        <ProfileForm
          website={""}
          updateBusinessData={updateBusinessData}
          businessData={businessData}
        />
      ),
    },
    {
      label: t("Posizione aziendale"),
      description: t(
        "Inserisci la sede della tua attività: miglioreremo prenotazioni, visibilità locale e precisione dei servizi. Anche se sei freelance o lavori online, indica comunque un punto di riferimento principale!"
      ),
      component: (
        <LocationForm updateBusinessData={updateBusinessData} businessData={businessData} />
      ),
    },
    {
      label: t("onboarding.logo"),
      description: t("onboardingDescriptions.logo"),
      component: <LogoForm updateBusinessData={updateBusinessData} businessData={businessData} />,
    },
    {
      label: t("onboarding.businessHours"),
      description: t("onboardingDescriptions.businessHours"),
      component: <BusinessScheduleOnboarding setScheduleToParent={setBusinessHours} />,
    },
    {
      label: t("onboarding.services"),
      description: t("onboardingDescriptions.services"),
      component: <ServicesForm initialValue={services} updateBusinessData={setBusinessServices} />,
    },
    {
      label: t("onboarding.employees"),
      description: t("onboardingDescriptions.employees"),
      component: (
        <EmployeeForm
          services={services}
          initialValue={employees}
          updateBusinessData={setBusinessEmployees}
        />
      ),
    },
    {
      label: "Aggiungi un altro business",
      description:
        "Devi aggiungere un altro? Nessun problema! Puoi farlo in qualsiasi momento, oppure puoi anche importare i dati della tua attività precedente.",
      component: (
        <AddAnotherBusinessOnboarding
          startFromScratch={startFromScratch}
          importFromPrevious={cloneBusiness}
        />
      ),
    },
  ];

  // Animate the step transition.
  const changeStep = (newStep: number) => {
    setContentVisible(false);
    setTimeout(() => {
      setActiveStep(newStep);
      setContentVisible(true);
    }, fadeDuration);
    // We'll validate the new step after it changes via the useEffect
  };

  // Validation functions for each step
  const validateStep = useCallback(
    (stepIndex: number): boolean => {
      setValidationErrors([]);
      let isValid = false;
      const errors: string[] = [];

      switch (stepIndex) {
        case 0: // Website step
          isValid = true;
          break;
        case 1: // Platform step
          isValid = !!businessData.name && !!businessData.category;
          if (!businessData.name)
            errors.push(t("Inserisci la tua ragione sociale o il nome della tua attività"));
          if (!businessData.category) errors.push(t("onboarding.businessCategoryRequired"));
          break;
        case 2: // Profile step
          isValid = !!businessData.about && !!businessData.email;
          if (!businessData.email) errors.push(t("Aggiungi la tua email"));
          if (!businessData.about) errors.push(t("Aggiungi una descrizione della tua attività"));
          break;
        case 3: // Location step
          isValid = !!businessData.address && !!businessData.city && !!businessData.country;
          !!businessData.postalCode;
          if (!businessData.postalCode) errors.push(t("Inserisci il C.A.P."));
          if (!businessData.address) errors.push(t("Inserisci l'indirizzo della tua attività"));
          if (!businessData.city) errors.push(t("Inserisci la città della tua attività"));
          if (!businessData.country) errors.push(t("Inserisci il paese della tua attività"));
          break;
        case 4: // Logo step
          isValid = true;
          /*console.log(businessData.logoUrl);
                    isValid = !!businessData.logoUrl;
                    if (!isValid) errors.push("Business logo is required");*/
          break;
        case 5: // Business Hours
          isValid = Object.keys(schedule).length > 0;
          if (!isValid) errors.push(t("onboarding.businessHoursRequired"));
          break;
        case 6: // Services
          isValid = services.length > 0;
          if (!isValid) errors.push(t("onboarding.servicesRequired"));
          break;
        case 7: // Employees
          isValid = employees.length > 0;
          if (!isValid) errors.push(t("onboarding.employeesRequired"));
          break;
        case 8: // Calendar and Social Media (optional)
          isValid = true; // Optional step
          break;
        default:
          isValid = true;
          break;
      }

      setValidationErrors(errors);
      setStepIsValid(isValid);
      return isValid;
    },
    [businessData, services, employees, schedule]
  );

  // Run validation on initial render and whenever step changes
  React.useEffect(() => {
    validateStep(activeStep);
  }, [activeStep, businessData, services, employees, schedule, validateStep]);

  // Modified handleNext to check validation before proceeding
  const handleNext = useCallback(async () => {
    const isCurrentStepValid = validateStep(activeStep);

    if (!isCurrentStepValid) {
      return; // Prevent navigation if step is not valid
    }

    if (activeStep === steps.length - 1) {
      await finalizeBusiness(true);
    } else {
      changeStep(Math.min(activeStep + 1, steps.length - 1));
    }
  }, [activeStep, steps.length, businessData, validateStep]);

  const handleBack = useCallback(() => {
    changeStep(Math.max(activeStep - 1, 0));
  }, [activeStep]);

  return {
    loading,
    loadingText,
    activeStep,
    steps,
    contentVisible,
    fadeDuration,
    handleNext,
    handleBack,
    stepIsValid,
    validationErrors,
    validateStep,
  };
};
